import moment from 'moment/moment'
import {
    chartLine,
    chartSymbol,
    exportExcelIcon,
    exportPictureIcon,
    fullScreenIcon,
    getAxisIntervalFormatter,
    getMonthKey,
    getMonthThreshold2,
    getYAxisNameGap,
    histogramIcon,
    legendSymbol,
    lineIcon,
    logIcon,
    setLogOptions,
    setYOptions,
    swapVertical,
    thresholdIcon,
    toEchartsData,
    yAutomaticScaleValues,
} from '../../../components/echart/EChartUtils'
import { getDateWithHour, getFullDate, getFullDateInRef } from '../../../utils/DateUtil'
import { useMeasuresTempRef } from '../../../utils/customHook/useMeasureTempRef'
import { getGmtSuffix } from '../../../utils/TempRefUtil'
import { findIndex, groupBy, isNil, isUndefined, keys, orderBy, round, sum, uniqBy, range, compact, max } from 'lodash'
import { DEFROUNDVALUE, hasBooleanValue, hasValue } from '../../../utils/NumberUtil'
import {
    generateGradient,
    getColorCircleEvent, getColorFromPalette,
    getColorFromPalette2,
    getEventColor,
    getRGBColor, getThresholdColor,
} from '../../../utils/ColorUtil'
import i18n from 'simple-react-i18n'
import { exportFile } from '../../../utils/ExportDataUtil'
import EChart from '../../../components/echart/EChart'
import React, { useEffect, useMemo, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import { IconStatus } from '../../../utils/StatusUtil'
import {
    chunkWithWords,
    getExportFormat,
    getI18nOrLabel,
    i18nExist,
} from '../../../utils/StringUtil'
import { getEventGraph } from '../../../utils/PiezometryUtils'
import CustomEventDuration from '../../../components/echart/series/CustomEventDuration'
import Axis from '../../../components/echart/Axis'
import Line from '../../../components/echart/series/Line'
import PropTypes from 'prop-types'
import HydrometryAction from '../../actions/HydrometryAction'
import EventsAction from '../../../events/actions/EventsAction'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import DtoHydrometricStation from '../../dto/DtoHydrometricStation'
import { getLabel } from '../../../utils/StoreUtils'
import DtoHydrometryThreshold from '../../dto/DtoHydrometryThreshold'
import DtoEvent from '../../../events/dto/DtoEvent'
import DtoHydroMeasures from '../../dto/chronicMeasures/DtoHydroMeasures'
import MessageCard from '../../../components/card/MessageCard'
import StationAction from '../../../station/actions/StationAction'
import { getQualifications, getStatuses } from '../../../utils/QualityUtils'
import Bar from '../../../components/echart/series/Bar'
import DtoPredMeasure from '../../../iaeau/dto/DtoPredMeasure'
import DtoPredStat from '../../../iaeau/dto/DtoPredStat'
import MultiBand from '../../../components/echart/series/MultiBand'
import { getMeasureJplus } from '../../../iaeau/utils/IAEauUtils'
import AnalysisAction from '../../../quality/actions/AnalysisAction'
import DtoAnalysisLight from '../../../quality/dto/analyse/DtoAnalysisLight'
import { calculateValue, getRemarks } from '../../../utils/AnalyseUtils'
import Scatter from '../../../components/echart/series/Scatter'
import { repeatList } from '../../../utils/ListUtil'
import { BAR, LINE } from 'components/echart/QualityChart'
import { CUMUL_PERSO } from '../../../utils/constants/MeasureConstants'
import { PASTEL_COLOR, PIEZOMETRY_COLOR } from '../../../utils/constants/ColorTheme'
import { HYDRO_MEASURE_COTE, HYDRO_TYPEID } from 'hydrometry/constants/HydrometryConstants'
import { IS_INITIAL_POINT, THRESHOLD_CATEGORIES } from 'station/constants/StationConstants'
import DialogScale from './dialogs/DialogScale'
import { getNGFDepth, toHydroNGFMeasures } from 'utils/HydroUtils'
import { getGeneralLevelling } from 'utils/SettingUtils'

const THRESHOLD = 0
const NOTHING = 2
const WATER_LEVEL_TYPE_ID = -1

const ChartReviewHydro = ({
    id,
    tempRef,
    hydroStatistics,
    fullScreen,
    setFullScreen,
    hydroMeasures,
    minDate,
    maxDate,
    showLegend = true,
    modelData,
    displayModes = [],
    displayCote = HYDRO_MEASURE_COTE.HEIGHT,
    returnPeriodSeries = [],
}) => {
    const {
        hydrometricStation,
        hydrometryThresholds,
        stationEvents,
        hydroDataTypes,
        iaeauModels,
        parameters,
        associatedSites,
        units,
        hydrometerChartOptions,
        hydrometryDataTypes,
    } = useSelector(store => ({
        hydrometryThresholds: store.HydrometryReducer.hydrometryThresholds,
        hydrometricStation: store.HydrometryReducer.hydrometricStation,
        stationEvents: store.EventsReducer.stationEvents,
        hydroDataTypes: store.StationReducer.hydroDataTypes,
        iaeauModels: store.IAEauReducer.iaeauModels,
        parameters: store.ParameterReducer.parameters,
        associatedSites: store.StationReducer.associatedSites,
        units: store.UnitReducer.units,
        hydrometerChartOptions: store.HydrometerStationReducer.chartOptions,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(HydrometryAction.fetchHydrometricThresholds())
        dispatch(HydrometryAction.fetchHydrometricStation(id))
        dispatch(EventsAction.fetchStationEvents('hydrometry', id))
        dispatch(StationAction.fetchDataTypesByProject('SIH'))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const filteredHydrometryThresholds = useMemo(() => hydrometryThresholds.filter(t => t.category !== THRESHOLD_CATEGORIES.CONTROL), [hydrometryThresholds])


    /*
    const chartMinDate = chartTab === 'HISTO' ? moment(minDate).startOf('year').valueOf() : minDate
    const chartMaxDate = chartTab === 'HISTO' ? moment(maxDate).add(1, 'year').startOf('year').valueOf() : maxDate
    */


    const [chartType, setChartType] = useState(LINE)
    const [stack, setStack] = useState()
    const [isLogActive, setIsLogActive] = useState(false)
    const [gridsSeries, setGridsSeries] = useState([])
    const [openModalGridsSeries, setOpenModalGridsSeries] = useState(false)
    const [yAxisOverrides, setYAxisOverrides] = useState({})
    const [displayLegend, setDisplayLegend] = useState(showLegend)
    const [stateThreshold, setStateThreshold] = useState(THRESHOLD)
    const [displayMarker, setDisplayMarker] = useState(false)
    const [displayLine, setDisplayLine] = useState(true)
    const [qualitoMeasures, setQualitoMeasures] = useState([])

    const symbolSize = displayMarker ? 6 : 0

    const hydroStatsFiltered = uniqBy(orderBy(hydroStatistics, ['order', 'typeId'], 'asc').filter(stat => !hasBooleanValue(stat.showData) || stat.showData), 'typeId')

    const ngfCote = useMemo(() => {
        if (displayCote === HYDRO_MEASURE_COTE.HEIGHT) return 0
        return getNGFDepth(hydrometricStation)
    }, [displayCote, hydrometricStation])

    const tempRefOffset = useMeasuresTempRef(hydrometricStation?.link_tempRefs, hydroStatsFiltered.map(stat => stat.typeId), tempRef)

    const [series, grids, xAxis, yAxis, gridsHeights] = [[], [], [], [], [65]]
    const axisLabelObj = getAxisIntervalFormatter(moment(maxDate), moment(minDate), undefined, tempRefOffset)
    const events = stationEvents.filter(e => {
        if (e.eventType === 'T') {
            return false
        }
        if (e.date) {
            const eventDate = getDateWithHour(e.date, e.eventHour).valueOf()
            return eventDate >= minDate && eventDate <= maxDate && e.graph == '1'
        }
        return false
    })

    const hydroMeasuresFiltered = useMemo(() => {
        if (!isLogActive) {
            return hydroMeasures
        }
        return hydroMeasures.map(hm => ({
            ...hm,
            measures: hm.measures.filter(m => m.value > 0),
        }))
    }, [isLogActive])

    const getHydrometerOption = (type) => hydrometerChartOptions.find(opt => type == opt.dataType) || {}

    const isHeightType = stat => stat.typeId === WATER_LEVEL_TYPE_ID || stat.typeId === HYDRO_TYPEID.HEIGHT

    const getDisplayLabel = stat => {
        if (!isHeightType(stat)) {
            return stat.label
        }

        return displayCote === HYDRO_MEASURE_COTE.NGF ? i18n.waterLevel : i18n.waterHeight
    }

    const getDisplayUnit = stat => {
        const unit = stat.unit
        if (!isHeightType(stat)) {
            return unit
        }

        if (displayCote !== HYDRO_MEASURE_COTE.NGF) {
            return hydroDataTypes.find(dataType => dataType.id === HYDRO_TYPEID.HEIGHT)?.unit
        }

        const generalLevelling = getGeneralLevelling()
        return unit.includes(generalLevelling) ? unit : `${unit} ${generalLevelling}`
    }

    const getModelSeries = (typeId, gridIndex, xAxisIndex, yAxisIndex, typeIdColor, cote = 0) => {
        const { predMeasures, selectedPred } = modelData
        const toEchartMeasure = (v, measure) => ({ value: [measure.date, round(v, 3), { measure }] })
        if (selectedPred && selectedPred[typeId] && predMeasures[typeId]?.length) {
            const pred = selectedPred[typeId].idModel ? { ...selectedPred[typeId], model: iaeauModels.find(m => m.idModel === selectedPred[typeId].idModel) } : selectedPred[typeId]
            const allSeries = groupBy(predMeasures[typeId], 'serieName')
            const nbLineSeries = Object.keys(allSeries).filter(key => hasValue(allSeries[key][0].value)).length
            const modelColor = pred?.model?.color ?? typeIdColor
            return Object.keys(allSeries).flatMap((serieName, idx) => {
                const orderedMeasures = orderBy(allSeries[serieName], 'date')
                const band = (() => {
                    if (hasValue(orderedMeasures[0].doubtMin)) {
                        const lower = {
                            showSymbol: false,
                            color: modelColor,
                            data: orderedMeasures.map(m => toEchartMeasure(m.doubtMin + cote, m)),
                            name: `${pred.source} - ${getI18nOrLabel(serieName) && serieName !== 'undefined' ? getI18nOrLabel(serieName) : 'Min'}`,
                            gridIndex,
                            xAxisIndex,
                            yAxisIndex,
                        }
                        const upper = {
                            showSymbol: false,
                            color: modelColor,
                            data: orderedMeasures.map(m => toEchartMeasure(m.doubtMax + cote, m)),
                            name: `${pred.source} - ${getI18nOrLabel(serieName) && serieName !== 'undefined' ? getI18nOrLabel(serieName) : 'Max'}`,
                            gridIndex,
                            xAxisIndex,
                            yAxisIndex,
                        }
                        return [MultiBand({ bands: [lower, upper], noSort: true, noGap: true, stack: `${pred.source}${idx}`, areaOpacity: 0.2 })]
                    }
                    return []
                })()
                const line = (() => {
                    if (hasValue(orderedMeasures[0].value)) {
                        return [Line({
                            data: orderedMeasures.map(m => toEchartMeasure(m.value + cote, m)),
                            name: `${pred.source} - ${getI18nOrLabel(serieName) && serieName !== 'undefined' ? getI18nOrLabel(serieName) : 'Prévision'}`,
                            color: nbLineSeries > 1 ? getColorFromPalette(idx) : modelColor,
                            connectNulls: true,
                            gridIndex,
                            xAxisIndex,
                            yAxisIndex,
                            lineStyle: {
                                normal: {
                                    color: nbLineSeries > 1 ? getColorFromPalette(idx) : modelColor,
                                    width: pred?.model?.lineWidth || 2,
                                    type: pred?.model?.lineType || 'dashed',
                                    opacity: pred?.model?.lineOpacity || 1,
                                },
                            },
                            itemStyle: {
                                normal: {
                                    color: nbLineSeries > 1 ? getColorFromPalette(idx) : modelColor,
                                },
                            },
                            showSymbol: false,
                            isPiezo: true,
                        })]
                    }
                    return []
                })()
                return [...band, ...line]
            })
        }
        return []
    }


    const addEvents = (series, grids, xAxis, yAxis, gridsHeights, axisLabelObj, events) => {
        const eventTooltip = {
            trigger: 'item',
            formatter: (params) => {
                const event = params.value?.[2] ?? params.data?.value?.[2]
                if (!event) return ''
                const startDate = getFullDate(getDateWithHour(event.date ?? event.startDate, event.eventHour).valueOf())
                const dateLine = event.endDate ? `${startDate} - ${getFullDate(event.endDate)}` : startDate
                const comment = event.comment ? chunkWithWords(event.comment, 40).replaceAll('\n', '<br />') : i18n.event
                return `${dateLine}<br />${getColorCircleEvent(event.eventType)} ${comment}`
            },
        }
        const eventOptions = {
            tooltip: eventTooltip,
            itemStyle: {
                normal: {
                    opacity: 0.5,
                },
            },
            yAxisIndex: 1,
        }
        series.push(getEventGraph(events, eventOptions))
        series.push(CustomEventDuration({
            name: i18n.events,
            serieId: 'events',
            yAxisIndex: 1,
            data: events.map(e => ({
                value: [getDateWithHour(e.date ?? e.startDate, e.eventHour).valueOf(), 1, e],
                itemStyle: { color: getRGBColor(getEventColor(e.eventType)) },
                tooltip: eventTooltip,
            })),
            renderItem: (_, api) => {
                const center = api.coord([api.value(0), 0])
                const height = api.size([0, 1])[1]
                const barWidth = 10
                return {
                    type: 'rect',
                    shape: {
                        x: center[0] - barWidth / 2,
                        y: center[1] - height / 2,
                        width: barWidth,
                        height,
                    },
                    style: api.style(),
                }
            },
        }))
        grids.push({
            top: sum(gridsHeights),
            right: '2%',
            height: 40,
            left: 100,
        })
        xAxis.push(Axis({
            type: 'time',
            position: 'bottom',
            min: minDate,
            max: maxDate,
            interval: axisLabelObj.interval,
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
        }))
        yAxis.push(Axis({
            type: 'value',
            data: [i18n.events],
            nameLocation: 'middle',
            minInterval: 1,
            nameGap: 40,
            position: 'right',
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
        }))
        yAxis.push(Axis({
            type: 'category',
            data: [i18n.events],
            nameLocation: 'middle',
            minInterval: 1,
            nameGap: 40,
            position: 'left',
        }))
        gridsHeights.push(60)
    }

    useEffect(() => {
        const idsNitrates = hydroStatsFiltered.map(stat => {
            return hydroDataTypes.find(h => h.id === stat.typeId)?.codeParameter
        }).filter(m => !isNil(m)).filter(p => !(p === ''))
        const assIdsQualito = associatedSites.filter(asso => asso.typeName === 'quality').map(att => att.stationLinkedId)
        AnalysisAction.getAnalysis({
            lightMode: true,
            stations: assIdsQualito,
            parameters: idsNitrates,
            support: '3', // support 'eau'
        }).then(json => setQualitoMeasures(groupBy(json.map(a => new DtoAnalysisLight(a)), 'qualitometer')))
    }, [])

    const getMeasuresByReference = (measures, isHeightGrid) => {
        if (isHeightGrid && displayCote !== HYDRO_MEASURE_COTE.HEIGHT) {
            return toHydroNGFMeasures(hydrometricStation, measures)
        }

        return measures
    }

    const addGrid = (series, grids, xAxis, yAxis, gridsHeights, axisLabelObj, stat, color, infoDataType) => {
        const isOtherHeight = stat.chronicFollowUp && stat.typeId !== HYDRO_TYPEID.HEIGHT
        if (!isOtherHeight) {
            grids.push({
                top: sum(gridsHeights),
                right: '2%',
                height: stat.typeId === HYDRO_TYPEID.HEIGHT || stat.typeId === HYDRO_TYPEID.FLOW ? 225 : 150,
                left: 100,
            })
        }
        const measure = hydroMeasuresFiltered.find(obj => obj.dataType === stat.typeId) || {}
        const displayLabel = getDisplayLabel(stat)
        const displayUnit = getDisplayUnit(stat)
        const measuresFormatted = getMeasuresByReference((measure.measures || []), (stat.typeId === HYDRO_TYPEID.HEIGHT || isOtherHeight))
        const measures = toEchartsData(measuresFormatted, undefined, undefined, undefined, symbolSize)

        const thresholdsFiltered = stateThreshold === THRESHOLD ? filteredHydrometryThresholds.filter(t => {
            return (!isUndefined(t.value)) && parseInt(t.stationId) === id && parseInt(t.dataType)=== stat.typeId
        }) : []
        const thresholds = isOtherHeight ? [] : getMeasuresByReference(thresholdsFiltered, (stat.typeId === HYDRO_TYPEID.HEIGHT || isOtherHeight)).map(t => ({
            yAxis: t.value,
            symbol: 'none',
            label: {
                show: true,
                position: 'middle',
                formatter: () => t.title ? `${t.title} : ${round(t.value, 10)} ${displayUnit ?? ''}` : '',
            },
            lineStyle: {
                normal: {
                    color: t.htmlColor || getThresholdColor(t.color),
                    type: 'dashed',
                },
            },
        }))

        const allpredMeasures = []
        if (measures.length) {
            getModelSeries(stat.typeId, grids.length - 1, isOtherHeight ? 1 : grids.length - 1, (isOtherHeight ? 1 : grids.length - 1) + 1, color, stat.typeId === HYDRO_TYPEID.HEIGHT ? ngfCote : 0).forEach(modelSerie => {
                series.push(modelSerie)
                allpredMeasures.push(modelSerie.obj.bands ? modelSerie.obj.bands.flatMap(b => b.data) : modelSerie.obj.data)
            })
        }

        const monthThresholds = filteredHydrometryThresholds.filter(t => {
            const isMonth = range(1, 13).some(m => hasValue(t[getMonthKey(m)]))
            return (isUndefined(t.value) && isMonth) && parseInt(t.stationId) === id && parseInt(t.dataType) === stat.typeId
        })
        const monthThresholdsValues = compact(monthThresholds.flatMap(monthThreshold => {
            return range(1, 13).flatMap(m => {
                const monthValue = monthThreshold[getMonthKey(m)]
                return monthValue
            })
        }))

        const scaleMeasures = stat.typeId === HYDRO_TYPEID.HEIGHT ?
            (() => {
                // when calculating height axis, we must considere every measure that are on the same grid as height (h.chroniFollowUp)
                const stats = hydroStatsFiltered.filter(h => h.chronicFollowUp && h.typeId !== HYDRO_TYPEID.HEIGHT)
                const statMeasures = stats.map(s => hydroMeasuresFiltered.find(obj => obj.dataType === s.typeId) || {})
                const statMeasuresFormatted = statMeasures.flatMap(statMeasure => {
                    return getMeasuresByReference((statMeasure.measures || []), true)
                })
                return [...measures, ...toEchartsData(statMeasuresFormatted, undefined, undefined, undefined, symbolSize)]
            })(measures) : measures

        const yScale = yAutomaticScaleValues([...scaleMeasures.map(m => m.value[1]), ...thresholds.map(t => t.yAxis), ...allpredMeasures.flat().map(d => d[1]), ...monthThresholdsValues])

        const getColor = (groupFunc, colorFunc) => {
            if (displayModes?.auto) return color
            if (groupFunc?.startsWith(CUMUL_PERSO)) return PASTEL_COLOR.orange
            const colors = {
                MIN: PASTEL_COLOR.red,
                MAX: PIEZOMETRY_COLOR.DEPTH,
                AVERAGE: PASTEL_COLOR.green,
                AUTO: color ?? 'black',
            }
            return colors[groupFunc] ?? colorFunc ?? 'black'
        }

        const measuresHydro = [measuresFormatted.map(m => ({
            ...m,
            groupFunc: measure.groupFunc,
            symbol: displayMarker ? 'circle' : 'none',
            unit: displayUnit ?? '',
            symbolSize,
        }))]

        const baseArgs = {
            overOptions: true,
            connectNulls: false,
            yAxisIndex: (isOtherHeight ? 1 : grids.length - 1) + 1,
            xAxisIndex: isOtherHeight ? 1 : grids.length - 1,
            serieId: `additional${stat.typeId}`,
            lineStyle: {
                type: infoDataType?.lineType ?? 'solid',
                opacity: displayLine ? (infoDataType?.lineOpacity ?? 1) : 0,
                width: infoDataType?.lineWidth ?? 2,
            },
            max: () => {
                if (isLogActive) {
                    return
                }
                return yScale.max
            },
            min: () => {
                if (isLogActive) {
                    return
                }
                if (chartType === BAR || stack) {
                    return 0
                }
                return yScale.min
            },
        }
        const serieFunc = stat.typeId === -2 ? Bar : (chartType === LINE ? Line : Bar)
        const formattedMonthThresholds = monthThresholds.map(monthThreshold => getMonthThreshold2(monthThreshold, minDate, maxDate, baseArgs))
        formattedMonthThresholds.forEach(formattedMonthThreshold =>
            series.push(formattedMonthThreshold),
        )

        if (stat.typeId === HYDRO_TYPEID.HEIGHT) {
            returnPeriodSeries.forEach(serie => {
                serie.updateObj({
                    yAxisIndex: (isOtherHeight ? 1 : grids.length - 1) + 1,
                    xAxisIndex: isOtherHeight ? 1 : grids.length - 1,
                })
                series.push(serie)
            })
        }

        const { predMeasures, selectedPred } = modelData
        const hasModelMeasures = selectedPred && selectedPred[stat.typeId] && predMeasures[stat.typeId]?.length
        if (hasModelMeasures) {
            series.push(Line({
                name: '',
                data: [],
                color: 'black',
                yAxisIndex: (isOtherHeight ? 1 : grids.length - 1) + 1,
                xAxisIndex: isOtherHeight ? 1 : grids.length - 1,
                markLine: {
                    label: { show: false },
                    symbol: 'none',
                    lineStyle: {
                        type: 'dashed',
                        width: 2,
                        color: 'grey',
                    },
                    data: [{
                        xAxis: moment().valueOf(),
                    }],
                },
            }))
        }

        measuresHydro.map(mHydro => {
            const groupFunc = mHydro[0]?.groupFunc
            if (mHydro.length && mHydro.find(m => mHydro[0].codepoint !== m.codepoint)) { // séries par point de prélèvement
                const colors = repeatList(generateGradient(color, '#FFF', 5), 10)
                const group = groupBy(mHydro, 'codepoint')
                keys(group).forEach((key, idx) => {
                    const marklines = (idx === 0 && thresholds.length) ? { markLine: { silent: false, symbol: 'none', precision: 10, data: thresholds } } : {}
                    series.push(serieFunc({
                        ...baseArgs,
                        data: toEchartsData(group[key], undefined, undefined, undefined, symbolSize),
                        name: `${displayLabel} ${i18nExist(groupFunc?.toLowerCase()) || ''} - ${hydrometricStation.link_pointPrels.find(p => p.point === parseInt(key))?.name ?? i18n.unknownPoint}`,
                        color: getColor(groupFunc, colors[idx]),
                        ...marklines,
                    }))
                })
            } else {
                const marklines = thresholds.length ? { markLine: { silent: false, symbol: 'none', precision: 10, data: thresholds } } : {}
                series.push(serieFunc({
                    ...baseArgs,
                    data: toEchartsData(mHydro, undefined, undefined, undefined, symbolSize),
                    name: `${displayLabel} ${i18nExist(groupFunc?.toLowerCase()) || ''}`,
                    color: getColor(groupFunc, color),
                    ...marklines,
                }))
            }
        })

        const parameter = parameters.find(p => p.code === stat.codeParameter)
        keys(qualitoMeasures).forEach(key => {
            const measure = qualitoMeasures[key]
                .filter(q => q.parameter === stat.codeParameter)
                .filter(m => m.analysisDate > minDate && m.analysisDate < maxDate)
                .sort((a, b) => a.analysisDate - b.analysisDate)
            if (measure.length) {
                const input = {
                    data: toEchartsData(uniqBy(measure, 'analysisDate').map(m => ({
                        date: m.analysisDate,
                        value: m.result,
                        symbol: getRemarks(m.remark)?.plot,
                        status: m.status,
                        qualification: m.qualification,
                        unit: getLabel(units, m.unit, 'symbol'),
                        isAnalysis: true,
                        analysis: m,
                    })), null, null, null, 10),
                    color,
                    showSymbol: true,
                    name: `${i18n.oneAnalysis} ${i18n.station} [${qualitoMeasures[key][0].qualitometer}] ${parameter.shortLabelWithCode ?? parameter.labelWithCode}`,
                    yAxisIndex: (isOtherHeight ? 1 : grids.length - 1) + 1,
                    xAxisIndex: isOtherHeight ? 1 : grids.length - 1,
                    serieId: `${stat.label} ${stat.codeParameter}`,
                }
                series.push(Scatter(input))
            }
        })
        if (!isOtherHeight) {
            xAxis.push(Axis({
                type: 'time',
                position: 'bottom',
                min: minDate,
                max: maxDate,
                interval: axisLabelObj.interval,
                gridIndex: grids.length - 1,
                axisLabel: { show: true, formatter: axisLabelObj.formatter },
                axisLine: { show: true },
                axisTick: { show: true },
                showSplitLine: true,
            }))

            const thresholdsValues = thresholdsFiltered.map(t => t.value)
            const graphicOption = getHydrometerOption(stat.typeId)
            const autoYOptions = setYOptions(graphicOption, yScale)
            const gridIndex = grids.length - 1
            // une borne personnalisée n'est retenue que si c'est un nombre fini (sinon auto-échelle)
            const overrideMin = yAxisOverrides[gridIndex]?.min
            const overrideMax = yAxisOverrides[gridIndex]?.max
            const finalMin = Number.isFinite(overrideMin) ? overrideMin : autoYOptions.min
            const finalMax = Number.isFinite(overrideMax) ? overrideMax : autoYOptions.max
            // Bornes personnalisées : l'interval de l'auto-échelle correspond à la plage des données, pas à la plage
            // affichée (ex : interval 4 sur -10/0 -> graduations -10/-6/-2/0). On le recalcule comme setYOptions.
            const boundsOverridden = finalMin !== autoYOptions.min || finalMax !== autoYOptions.max
            const finalInterval = boundsOverridden && Number.isFinite(finalMin) && Number.isFinite(finalMax) && finalMax > finalMin
                ? yAutomaticScaleValues([finalMin, finalMax]).interval
                : autoYOptions.interval
            const { nameGap, gridLeft } = getYAxisNameGap(finalMin, finalMax, autoYOptions.axisLabel?.formatter)
            grids[gridIndex].left = Math.max(grids[gridIndex].left ?? 100, gridLeft)
            yAxis.push(Axis({
                type: isLogActive ? 'log' : 'value',
                nameLocation: 'middle',
                name: displayUnit ? `${displayLabel} [${displayUnit}]` : displayLabel,
                gridIndex,
                nameGap,
                showSplitLine: hasValue(graphicOption?.displayXIntervalYear) ? graphicOption.displayXIntervalYear === '1' : true,
                ...autoYOptions,
                min: finalMin,
                max: finalMax,
                interval: finalInterval,
                ...setLogOptions(isLogActive, [...thresholdsValues, ...measuresHydro.map(t => t.value)]),
            }))
            gridsHeights.push(stat.typeId === HYDRO_TYPEID.HEIGHT || stat.typeId === HYDRO_TYPEID.FLOW ? 265 : 190)
        }
    }

    const getExportData = () => {
        const isMultiPoint = hydroStatistics.some(stat => hydroStatistics.some(stat2 => stat.typeId === stat2.typeId && stat.codepoint !== stat2.codepoint))
        const data = hydroStatistics.flatMap(stat => {
            const isOtherHeight = stat.chronicFollowUp && stat.typeId !== HYDRO_TYPEID.HEIGHT

            const measure = hydroMeasuresFiltered.find(obj => obj.dataType === stat.typeId && obj?.measures?.[0]?.codepoint === stat.codepoint) || {}
            const measuresFormatted = getMeasuresByReference((measure.measures || []), (stat.typeId === HYDRO_TYPEID.HEIGHT || isOtherHeight))

            const roundValue = hydrometryDataTypes.find(hdt => hdt.id === stat.typeId)?.numberDecimal || DEFROUNDVALUE
            return measuresFormatted.map(d => ({
                stationCode: { value: hydrometricStation.code },
                stationName: { value: hydrometricStation.name },
                date: { value: getFullDateInRef(d.date, tempRefOffset), format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date' },
                value: { value: d.value, format: getExportFormat(roundValue), cellType: 'number' },
                type: { value: isMultiPoint ? `${stat.label} - ${stat.namePoint}` : stat.label },
                status: { value: getLabel(getStatuses(), d.status), cellType: 'string' },
                qualification: { value: getLabel(getQualifications(), d.qualification), cellType: 'string' },
                initialPoint: { value: d.initialPoint === IS_INITIAL_POINT ? i18n.initialPoint : '' },
            }))
        })

        if (data.length) {
            data[0].headers = ['stationCode', 'stationName', 'date', 'value', 'type', 'status', 'qualification', 'initialPoint']
        }
        return data
    }

    addEvents(series, grids, xAxis, yAxis, gridsHeights, axisLabelObj, events)

    const filterResult = hydroStatsFiltered.filter(c => !c.chronicFollowUp || c.typeId == HYDRO_TYPEID.HEIGHT || c.showData === true)
    if (!filterResult.length) {
        return <div style={{ marginTop: 20 }}><MessageCard><span>{`${i18n.error} : ${i18n.chartDisplay}`}</span></MessageCard></div>
    }
    const hasOtherHeight = filterResult.some(stat => stat.chronicFollowUp && stat.typeId !== HYDRO_TYPEID.HEIGHT)
    const hasHeightType = filterResult.some(stat => stat.typeId === HYDRO_TYPEID.HEIGHT)

    if (hasOtherHeight && !hasHeightType) {
        grids.push({
            top: sum(gridsHeights),
            right: '2%',
            height: 150,
            left: 100,
        })
        xAxis.push(Axis({
            type: 'time',
            position: 'bottom',
            min: minDate,
            max: maxDate,
            interval: axisLabelObj.interval,
            gridIndex: grids.length - 1,
            axisLabel: { show: true, formatter: axisLabelObj.formatter },
            axisLine: { show: true },
            axisTick: { show: true },
            showSplitLine: true,
        }))
        yAxis.push(Axis({
            type: 'value',
            nameLocation: 'middle',
            name: '',
            gridIndex: grids.length - 1,
            nameGap: 40,
            showSplitLine: true,
        }))
        gridsHeights.push(190)
    }

    filterResult.forEach((stat, idx) => {
        const infoDataType = hydroDataTypes.find(h => h.id === stat.typeId)
        addGrid(series, grids, xAxis, yAxis, gridsHeights, axisLabelObj, { ...stat, ...infoDataType }, stat.color || getColorFromPalette2(idx), infoDataType)
    })
    const maxGridLeft = max([...grids.map(g => g.left ?? 100), 100])
    grids.forEach(g => {
        g.left = maxGridLeft
    })
    const chartInnerHeight = fullScreen ? window.innerHeight / 1.5 : window.innerHeight / 2.3
    const updateHeightGrid = grids.filter(g => g.height === 150 || g.height === 225).length === 1
    let sumGridsHeight = sum(gridsHeights)
    if (updateHeightGrid) {
        const gridIndex = findIndex(grids, g => g.height === 150 || g.height === 225)
        sumGridsHeight = sumGridsHeight - grids[gridIndex].height + chartInnerHeight - 40
        grids[gridIndex].height = chartInnerHeight
    }

    // these functions are called in the option object as well as in the setOption in the mouseEvent
    const getLegend = () => ({
        top: '10px',
        left: '5%',
        right: '10px',
        type: 'scroll',
        show: displayLegend,
    })

    const stationTitle = hydrometricStation.name || hydrometricStation.code || ''

    const getToolbox = () => ({
        top: '35px',
        right: '10px',
        showTitle: false,
        itemSize: 18,
        tooltip: {
            show: true,
            position: 'bottom',
        },
        feature: {
            myToolLine: {
                show: true,
                title: i18n.lines,
                icon: lineIcon,
                onclick: () => {
                    setStack(undefined)
                    setChartType(LINE)
                },
                iconStyle: {
                    borderColor: chartType === LINE ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolBar: {
                show: true,
                title: i18n.histogram,
                icon: histogramIcon,
                onclick: () => {
                    setStack(undefined)
                    setChartType(BAR)
                    setIsLogActive(false)
                },
                iconStyle: {
                    borderColor: chartType === BAR && !stack ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolLog: {
                show: true,
                title: i18n.logarithm,
                icon: logIcon,
                onclick: () => {
                    setStack(undefined)
                    setChartType(LINE)
                    setIsLogActive(prevDisplayLog => !prevDisplayLog)
                },
                iconStyle: {
                    borderColor: isLogActive ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolThreshold: {
                show: !!filteredHydrometryThresholds.length,
                title: i18n.toggleThreshold,
                icon: thresholdIcon,
                onclick: () => setStateThreshold(prevStateThreshold => {
                    return prevStateThreshold === THRESHOLD ? NOTHING : THRESHOLD
                }),
                iconStyle: {
                    borderColor: stateThreshold !== NOTHING ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolToggleLegend: {
                show: true,
                title: i18n.toggleLegend,
                icon: legendSymbol,
                onclick: () => setDisplayLegend(prevHiddenLegend => !prevHiddenLegend),
                iconStyle: {
                    borderColor: displayLegend ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolToggleMarker: {
                show: true,
                title: i18n.toggleMarker,
                icon: chartSymbol,
                onclick: () => setDisplayMarker(prevHiddenMarker => !prevHiddenMarker),
                iconStyle: {
                    borderColor: displayMarker ? '#4d93c9' : '#5c5c5c',
                },
            },
            myToolToggleLine: {
                show: true,
                title: i18n.toggleLine,
                icon: chartLine,
                onclick: () => setDisplayLine(prevHiddenLine => !prevHiddenLine),
                iconStyle: {
                    borderColor: displayLine ? '#4d93c9' : '#5c5c5c',
                },
            },
            saveAsImage: {
                show: true,
                title: i18n.pictureExport,
                icon: exportPictureIcon,
                name: `${i18n.graph} ${stationTitle}`,
            },
            myToolExport: {
                show: true,
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: () => {
                    exportFile({
                        data: getExportData(),
                        exportType: 'xlsx',
                        titleFile: `${i18n.overview} ${stationTitle}`,
                    })
                },
            },
            myToolFullScreen: {
                show: true,
                title: i18n.fullScreen,
                icon: fullScreenIcon,
                onclick: () => setFullScreen(!fullScreen),
            },
            myZoom: {
                show: true,
                title: i18n.zoomVertical,
                icon: swapVertical,
                onclick: () => {
                    const newGridsSeries = []
                    const newOverrides = {}

                    filterResult.forEach(stat => {
                        const infoDataType = hydroDataTypes.find(h => h.id === stat.typeId)
                        const isOtherHeight = stat.chronicFollowUp && stat.typeId !== HYDRO_TYPEID.HEIGHT

                        if (!isOtherHeight) {
                            const graphicOption = getHydrometerOption(stat.typeId)
                            const measure = hydroMeasuresFiltered.find(obj => obj.dataType === stat.typeId) || {}
                            const measuresFormatted = getMeasuresByReference(
                                (measure.measures || []),
                                (stat.typeId === HYDRO_TYPEID.HEIGHT || isOtherHeight),
                            )

                            const yScale = yAutomaticScaleValues(
                                measuresFormatted.map(m => m.value),
                            )
                            const autoYOptions = setYOptions(graphicOption, yScale)
                            const gridIndex = newGridsSeries.length + 1

                            newOverrides[gridIndex] = yAxisOverrides[gridIndex] ?? {
                                min: autoYOptions.min,
                                max: autoYOptions.max,
                            }
                            newGridsSeries.push(infoDataType)
                        }
                    })
                    setGridsSeries(newGridsSeries)
                    setYAxisOverrides(prev => ({
                        ...newOverrides,
                        ...prev,
                    }))
                    setOpenModalGridsSeries(true)
                },
            },
            restore: {
                show: true,
                title: i18n.restore,
            },
        },
    })

    const options = {
        series,
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const measureParams = params.filter(o => o.seriesName !== i18n.events)
                const refParam = measureParams[0] ?? params[0]
                const refDate = refParam.value?.[2]?.date ?? refParam.value?.[0]
                const date = `${getFullDateInRef(refDate, tempRefOffset)}${getGmtSuffix(tempRefOffset)}`
                const paramsOrder = orderBy(measureParams.map(o => {
                    const hydroStats = hydroStatistics.find(h => h.label === o.seriesName ? h.unit : '')
                    const unit = hydroStats?.unit ?? o.data.unit
                    const value = o.data?.realValue ?? o.value[2]?.value ?? o.value?.[1]
                    return ({
                        seriesIndex: o.seriesIndex,
                        marker: o.marker,
                        seriesName: o.seriesName,
                        value: value > 1 ? round(value, 2) : round(value, 3),
                        unit: unit ? `[${unit}] ` : '',
                        status: ReactDOMServer.renderToString(o.value[2] ?
                            <IconStatus status={o.value[2].status} qualification={o.value[2].qualification} size={'20px'} />
                            :
                            <IconStatus size={'20px'} />,
                        ),
                        jPlus: getMeasureJplus(o.data?.value[2]?.measure),
                        isAnalysis: o.value[2]?.isAnalysis,
                        analysis: o.value[2]?.analysis,
                    })
                }), 'seriesIndex', 'asc')
                const stationEventsFiltered = stationEvents.filter(o => moment(o.date).isSame(refDate, 'day') && o.graph == '1' && o.eventType !== 'T')
                const labelEvents = stationEventsFiltered.map(v => {
                    const comment = v.comment ? chunkWithWords(v.comment, 40).replaceAll('\n', '<br />') : i18n.event
                    return `${getColorCircleEvent(v.eventType)} ${comment}`
                }).join('<br />')
                const result = paramsOrder.map(o => (`<span style="display:flex;align-items:center;">${o.marker} ${o.seriesName} : ${o.isAnalysis ? calculateValue(o.analysis) : o.value} ${o.unit} ${o.jPlus}<span style="padding-left:5px">${o.status}</span> </span>`)).join('')
                return [date, labelEvents, result].filter(s => s).join('<br />')
            },
        },
        grid: grids,
        xAxis,
        yAxis,
        axisPointer: {
            link: { xAxisIndex: 'all' },
        },
        setDataZoom: true,
        height: sumGridsHeight,
        toolbox: getToolbox(),
        legend: getLegend(),
    }
    return !!options.series.length && (
        <>
            <EChart
                id='hydroDashboardChart'
                options={options}
                scrollable={fullScreen}
            />
            <DialogScale
                open={openModalGridsSeries}
                setOpen={setOpenModalGridsSeries}
                grids={gridsSeries}
                setGrids={setGridsSeries}
                yAxis={yAxisOverrides}
                setYAxis={setYAxisOverrides}
            />

        </>
    )
}

ChartReviewHydro.propTypes = {
    tempRef: PropTypes.number,
    hydrometricStation: PropTypes.instanceOf(DtoHydrometricStation),
    hydrometryThresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometryThreshold)),
    stationEvents: PropTypes.arrayOf(PropTypes.instanceOf(DtoEvent)),
    id: PropTypes.number,
    hydroStatistics: PropTypes.arrayOf(PropTypes.shape({
        typeId: PropTypes.number,
        label: PropTypes.string,
    })),
    fullScreen: PropTypes.bool,
    setFullScreen: PropTypes.func,
    hydroMeasures: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydroMeasures)),
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    showLegend: PropTypes.bool,
    modelData: PropTypes.shape({
        predMeasures: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.instanceOf(DtoPredMeasure))),
        selectedPred: PropTypes.objectOf(PropTypes.instanceOf(DtoPredStat)),
        predStats: PropTypes.arrayOf(PropTypes.instanceOf(DtoPredStat)),
    }),
    displayModes: PropTypes.shape({
        auto: PropTypes.bool,
        brute: PropTypes.bool,
        average: PropTypes.bool,
        min: PropTypes.bool,
        max: PropTypes.bool,
    }),
    displayCote: PropTypes.number,
    returnPeriodSeries: PropTypes.arrayOf(PropTypes.shape({})),
}

export default ChartReviewHydro
